import React, { useState, useEffect } from 'react'
import { ResolveFailureCounterChart } from './ResolveFailureCounterChart'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { mapBuckets } from '../common/labels'
import { HelpPopupButton } from '@/popups/helppopup/help'
import { useModal } from '@/components/modal/Modal'
import { FailureCounterPopupBody } from './FailureCounterPopup'

type ResolveFailureCounterBoxProps = { 
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  goToMachinePage: (machineId: string) => void
}

// Interface representing a single data entry
export interface ResolveFailureCounterData {
  bucket: string     // ISO timestamp string
  failureCount: number // number of failure devices
}

// Interface representing the full API response (array of entries)
export type ResolveFailureCounterResponse = ResolveFailureCounterData[]

export const ResolveFailureCounterBox = ({ services, session, criteria, goToMachinePage }: ResolveFailureCounterBoxProps) => {
  const translates = useTranslations(services, session)
  const { openModal } = useModal()
  const [response, setResponse] = useState<ResolveFailureCounterResponse>()

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/machine/resolvefailurecounter?${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then((data) => {
        setResponse(data)
      })
  }, [services.session, criteria])

  if (translates === undefined || !response) {
    return <div className='spinner'></div>
  }

  // Prepare labels and data for the chart
  const labels = response.map(mapBuckets)
  const failureCounts = response.map(row => row.failureCount)

  return (
    <div>
      <HelpPopupButton helpKey='page.dashboard.global.box.machine_resolvefailurecounter.help' translates={translates}/>
      <ResolveFailureCounterChart
        labels={labels}
        dataSet={failureCounts}
        chartTitle={translates.for('page.dashboard.global.box.machine_resolvefailurecounter.title')}
        dataLabel={translates.for('page.dashboard.global.box.machine_resolvefailurecounter.data_label')}
        onClick={(i) => openModal((props) => FailureCounterPopupBody({
          ...props,
          services,
          session,
          criteria,
          bucketFor: new Date(response[i].bucket),
          goToMachinePage,
          endpoint: 'resolved-failures'
        }))}
      />
    </div>
  )
}
